import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { OrderDTO } from '@/models/OrderDTO';
import { customerPaymentsApi, ordersApi, paymentApi } from '../../services/rest/apiInstances';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

export const fetchOrderDetails = createAsyncThunk(
    'orders/fetchOrderDetails',
    async (orderReference: string) => {
        try {
            const options = buildClientApiConfig();
            const data = await ordersApi.getOrder({ orderReference }, options);
            return data.data;
        } catch (error: any) {
            return error.data;
        }
    },
);

export const fetchOrderPaymentInstructions = createAsyncThunk(
    'orders/fetchOrderPaymentInstructions',
    async ({ orderId, orderReference }: { orderId?: string; orderReference?: string }) => {
        try {
            const options = buildClientApiConfig();
            const data = await paymentApi.listPayments(
                orderId ? { orderId } : { orderReference },
                options,
            );
            return data.data.payments;
        } catch (error: any) {
            return error.data;
        }
    },
);

export const fetchOrderInboundPayments = createAsyncThunk(
    'orders/fetchOrderInboundPayments',
    async ({ orderId, orderReference }: { orderId?: string; orderReference?: string }) => {
        try {
            const options = buildClientApiConfig();
            const data = await customerPaymentsApi.listCustomerPayments(
                // @ts-ignore
                orderId ? { orderId } : { orderReference },
                options,
            );
            return data.data.payments;
        } catch (error: any) {
            return error.data;
        }
    },
);

export interface OrderEditFormState {
    triggers: any;
    orderDetails: OrderDTO | null;
    isPending: boolean;
    errors: any;
    isValidDetailsForm: boolean;
    editedOrderDetails: any;
    isPaymentInstructionsPending: boolean;
    isBlockedNavigation: boolean;
    paymentInstructions: any[] | null;
    inboundPayments: any[] | null;
    isInboundPaymentsPending: boolean;
}

const initialState: OrderEditFormState = {
    triggers: null,
    orderDetails: null,
    isPending: false,
    errors: null,
    isValidDetailsForm: false,
    editedOrderDetails: null,
    isPaymentInstructionsPending: false,
    isBlockedNavigation: false,
    paymentInstructions: null,
    inboundPayments: null,
    isInboundPaymentsPending: false,
};

export const OrderEditFormSlice = createSlice({
    name: 'orderEditForm',
    initialState,
    reducers: {
        addTrigger: (state, action: PayloadAction<any>) => {
            state.triggers = state.triggers
                ? state.triggers.concat(action.payload)
                : [action.payload];
        },
        clearTriggers: (state) => {
            state.triggers = null;
        },
        executeTriggers: (state) => {
            const triggers = state.triggers;
            if (triggers && triggers?.length > 0) {
                triggers.forEach((trigger: any) => {
                    trigger();
                });
            }
        },
        clearOrderDetails: (state) => {
            state.orderDetails = null;
        },
        setDetailsFormValidity: (state, action: PayloadAction<boolean>) => {
            state.isValidDetailsForm = action.payload;
        },
        setBlockedNavigation: (state, action: PayloadAction<boolean>) => {
            state.isBlockedNavigation = action.payload;
        },
        setEditedOrderDetails: (state, action) => {
            state.editedOrderDetails = action.payload;
        },
        setPending: (state, action: PayloadAction<boolean>) => {
            state.isPending = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrderDetails.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchOrderDetails.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchOrderDetails.fulfilled, (state, action) => {
                const order = action.payload;
                state.isPending = false;
                state.orderDetails = order;
            })
            .addCase(fetchOrderPaymentInstructions.pending, (state) => {
                state.isPaymentInstructionsPending = true;
                state.paymentInstructions = null;
            })
            .addCase(fetchOrderPaymentInstructions.rejected, (state, action) => {
                state.isPaymentInstructionsPending = false;
                state.errors = action.error;
            })
            .addCase(fetchOrderPaymentInstructions.fulfilled, (state, action) => {
                const payments = action.payload;
                state.isPaymentInstructionsPending = false;
                state.paymentInstructions = payments;
            })
            .addCase(fetchOrderInboundPayments.pending, (state) => {
                state.isInboundPaymentsPending = true;
                state.inboundPayments = null;
            })
            .addCase(fetchOrderInboundPayments.rejected, (state, action) => {
                state.isInboundPaymentsPending = false;
                state.errors = action.error;
            })
            .addCase(fetchOrderInboundPayments.fulfilled, (state, action) => {
                const payments = action.payload;
                state.isInboundPaymentsPending = false;
                state.inboundPayments = payments;
            });
    },
});

export const {
    addTrigger,
    clearTriggers,
    executeTriggers,
    clearOrderDetails,
    setBlockedNavigation,
    setDetailsFormValidity,
    setEditedOrderDetails,
    setPending,
} = OrderEditFormSlice.actions;

export default OrderEditFormSlice.reducer;
